import React, { useCallback } from 'react';

import { SnapshotAction, track } from '../../global/tracking';
import { DiffStatus, DiffViewFragmentFragment } from '../../graphql/__generated__/graphql';
import DecideButton, { DecideButtonTypes } from '../common/DecideButton';
import KeyBind from '../common/KeyBind';
import { DiffViewMenu } from './components/DiffViewMenu';

type Props = {
  diff: DiffViewFragmentFragment
  isAccepted: boolean
  isRejected: boolean
  onAccept: () => void
  onReject: () => void
  onUnapprove: () => void
}

const DiffReviewButtons: React.FC<Props> = ({
  diff,
  isAccepted,
  isRejected,
  onAccept,
  onReject,
  onUnapprove,
}) => {
  const onClickAccept = useCallback(() => {
    onAccept();
    track.snapshotReviewed(SnapshotAction.Approve);
  }, [onAccept]);

  const onClickReject = useCallback(() => {
    onReject();
    track.snapshotReviewed(SnapshotAction.Reject);
  }, [onReject]);

  const onClickUnapprove = useCallback(() => {
    onUnapprove();
    track.snapshotReviewed(SnapshotAction.Unapprove);
  }, [onUnapprove]);

  return (
    <>
      <div className="flex flex-col gap-2 text-gray-600 absolute top-2 right-2 z-20 items-end">
        <div
          className="flex shadow-md bg-white rounded"
          data-qa="diff-review-actions"
        >
          <KeyBind
            char={isAccepted ? 'U' : 'A'}
            func={isAccepted ? onClickUnapprove : onClickAccept}
            label={isAccepted ? 'Undo' : 'Accept'}
          >
            <DecideButton
              aria-label="Accept Snapshot"
              className="rounded-tr-none rounded-br-none"
              isActive={isAccepted}
              isNoChanges={diff.status === DiffStatus.Equal}
              onClick={onClickAccept}
              onUnapprove={onClickUnapprove}
              type={DecideButtonTypes.ACCEPT}
            />
          </KeyBind>
          <div className="h-auto border-0 border-r border-gray-200 border-solid" />
          <KeyBind
            char={isRejected ? 'U' : 'R'}
            func={isRejected ? onClickUnapprove : onClickReject}
            label={isRejected ? 'Undo' : 'Reject'}
          >
            <DecideButton
              aria-label="Reject Snapshot"
              className="rounded-bl-none rounded-tl-none"
              isActive={isRejected}
              onClick={onClickReject}
              onUnapprove={onClickUnapprove}
              type={DecideButtonTypes.REJECT}
            />
          </KeyBind>
        </div>
        <DiffViewMenu />
      </div>
    </>
  );
};

export default DiffReviewButtons;
